import React from 'react';
import styles from './MentionComposer.module.scss';
import {
  MENTION_CATEGORIES,
  MentionCategory,
  MentionValues,
  filterMentionCandidates,
} from './mentionUtils';

/** Rows shown per category before "+N more" is offered. */
export const DEFAULT_CAP = 8;

/** How many more rows each "+N more" click reveals. */
export const CAP_STEP = 50;

/**
 * The cap after another "+N more" click.
 *
 * Each click adds a page, so a long list can be walked all the way down --
 * treating expansion as a boolean would let it grow exactly once and then
 * silently ignore every later click.
 */
export function nextExpandedCap(current: number | undefined): number {
  if (!current || current <= DEFAULT_CAP) return CAP_STEP;
  return current + CAP_STEP;
}

const GROUP_TITLES: Record<MentionCategory, string> = {
  practitioner: 'PRACTITIONER',
  product: 'PRODUCT',
  city: 'CITY',
};

/**
 * Channel label -> its style. Matched case-insensitively so a label cached by
 * an older build still gets its colour rather than rendering white-on-white.
 */
const CHANNEL_STYLES: Record<string, string> = {
  direct: styles.channelDirect,
  distributor: styles.channelDistributor,
  'direct and distributor': styles.channelBoth,
};

const channelStyle = (label: string): string =>
  CHANNEL_STYLES[label.trim().toLowerCase()] || '';

export interface MentionRow {
  category: MentionCategory;
  value: string;
}

interface MentionGroup {
  category: MentionCategory;
  items: string[];
  total: number;
}

/**
 * Which rows the panel is showing, flattened in display order.
 *
 * Exported because the composer needs the same list to resolve activeIndex when
 * a key is pressed -- the panel must never disagree with the keyboard about
 * which row is row 3.
 */
export function buildMentionRows(
  values: MentionValues,
  query: string,
  lockedCategory: MentionCategory | null,
  /** Per-category row cap, absent meaning DEFAULT_CAP. */
  expanded: Partial<Record<MentionCategory, number>>,
): { groups: MentionGroup[]; rows: MentionRow[] } {
  const groups: MentionGroup[] = [];
  const rows: MentionRow[] = [];

  for (const category of MENTION_CATEGORIES) {
    // Once a value is picked, the other categories disappear -- one @-token
    // resolves to exactly one category.
    if (lockedCategory && category !== lockedCategory) continue;

    const cap = expanded[category] || DEFAULT_CAP;
    const { items, total } = filterMentionCandidates(values[category] || [], query, cap);
    if (total === 0) continue;

    groups.push({ category, items, total });
    for (const value of items) rows.push({ category, value });
  }

  return { groups, rows };
}

interface Props {
  groups: MentionGroup[];
  rows: MentionRow[];
  activeIndex: number;
  selected: string[];
  /** Values are still being fetched for this rep. */
  loading: boolean;
  /** practitioner name -> channel label. Empty for the other categories. */
  channels: Record<string, string>;
  onSelect: (row: MentionRow) => void;
  onExpand: (category: MentionCategory) => void;
  onHoverRow: (index: number) => void;
}

const MentionPanel: React.FC<Props> = ({
  groups,
  rows,
  activeIndex,
  selected,
  loading,
  channels,
  onSelect,
  onExpand,
  onHoverRow,
}) => {
  if (groups.length === 0) {
    // An empty list means one of two different things, and saying "No matches"
    // while the first fetch is still running is simply untrue -- that query can
    // take a few seconds on a cold cache.
    return (
      <div className={styles.panel} data-mention-composer>
        {loading ? (
          <div className={styles.loading} role="status">
            Loading practitioners, products and cities…
          </div>
        ) : (
          <div className={styles.noResults}>No matches</div>
        )}
      </div>
    );
  }

  return (
    <div
      className={styles.panel}
      id="mention-listbox"
      role="listbox"
      aria-multiselectable="true"
      aria-label="Mention suggestions"
      data-mention-composer
    >
      {groups.map((group) => (
        <div key={group.category} role="group" aria-labelledby={`mention-group-${group.category}`}>
          <div id={`mention-group-${group.category}`} className={styles.groupHeader} role="presentation">
            {GROUP_TITLES[group.category]}
          </div>

          {group.items.map((value) => {
            const index = rows.findIndex((r) => r.category === group.category && r.value === value);
            const isActive = index === activeIndex;
            const isSelected = selected.indexOf(value) !== -1;
            const channel = group.category === 'practitioner' ? channels[value] : undefined;

            return (
              <div
                key={`${group.category}:${value}`}
                id={`mention-opt-${index}`}
                role="option"
                aria-selected={isSelected}
                className={[
                  styles.row,
                  isActive ? styles.rowActive : '',
                  isSelected ? styles.rowSelected : '',
                ].filter(Boolean).join(' ')}
                // Without this the textarea blurs on click, the caret position is
                // lost, and the token gets inserted in the wrong place.
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => onHoverRow(index)}
                onClick={() => onSelect({ category: group.category, value })}
              >
                <span className={styles.rowCheck} aria-hidden="true">{isSelected ? '✓' : ''}</span>
                <span className={styles.rowLabel}>{value}</span>
                {channel && (
                  <span className={`${styles.channel} ${channelStyle(channel)}`}>
                    {channel}
                  </span>
                )}
              </div>
            );
          })}

          {group.total > group.items.length && (
            <div
              className={styles.moreRow}
              role="presentation"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onExpand(group.category)}
            >
              +{group.total - group.items.length} more
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default MentionPanel;
